import type { Meeting } from "@repo/types";
import type { DotNotation } from "@repo/lib/dotPath";
import {
  BaseExportConverters,
  BaseTableExportService,
  type FieldConverter,
} from "./baseTableExportService";
import { tenantStore } from "@/stores/tenantStore";
import { agendaRepo } from "@/repositories/agendaRepo";
import { firstValueFrom } from "rxjs";

const baseExportConverters = new BaseExportConverters();

export class MeetingExportService extends BaseTableExportService<Meeting> {
  public constructor() {
    super({
      id: {
        alias: "id",
        columns: {
          value: "id",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "ID",
      },
      name: {
        alias: "name",
        columns: {
          value: "name",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Name",
      },
      tags: {
        alias: "tags",
        columns: {
          value: "tags",
        },
        fieldConverter: baseExportConverters.tagsConverter,
        resultFieldname: "Meeting Tags",
      },
      nestedTags: {
        alias: "$nestedTags",
        columns: {
          value: "$nestedTags" as DotNotation<Meeting>, // TODO - $nestedTags not in a Meeting type
        },
        fieldConverter: baseExportConverters.tagsConverter,
        resultFieldname: "Agenda Item Tags",
      },
      description: {
        alias: "description",
        columns: {
          value: "description",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Description",
      },
      status: {
        alias: "status",
        columns: {
          value: "status",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Status",
      },
      meetingType: {
        alias: "meetingType",
        columns: {
          value: "meetingType",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Meeting Type",
      },
      unpublishedEdits: {
        alias: "editedSincePublished",
        columns: {
          publishedAt: "publishedAt",
          agendaPublishedAt: "agendaPublishedAt",
          updatedAt: "updatedAt",
        },
        fieldConverter: unpublishedEditsConverter,
        resultFieldname: "Unpublished Edits",
      },
      agenda: {
        alias: "agenda",
        columns: {
          value: "agendaId",
        },
        fieldConverter: agendaConverter,
        resultFieldname: "Agenda",
      },
      meetingDate: {
        alias: "meetingDate",
        columns: {
          value: "startTime",
        },
        fieldConverter: baseExportConverters.formatDateConverter,
        resultFieldname: "Meeting Date",
      },
      meetingTime: {
        alias: "meetingTime",
        columns: {
          value: "startTime",
        },
        fieldConverter: baseExportConverters.formatTimeConverter,
        resultFieldname: "Meeting Time",
      },
      createdAt: {
        alias: "createdAt",
        columns: {
          value: "createdAt",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Created",
      },
      createdBy: {
        alias: "createdBy",
        columns: {
          value: "createdBy",
        },
        fieldConverter: baseExportConverters.userConverter,
        resultFieldname: "Created By",
      },
      updatedAt: {
        alias: "updatedAt",
        columns: {
          value: "updatedAt",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Last Modified",
      },
      updatedBy: {
        alias: "updatedBy",
        columns: {
          value: "updatedBy",
        },
        fieldConverter: baseExportConverters.userConverter,
        resultFieldname: "Modified By",
      },
      teamId: {
        alias: "teamId",
        columns: {
          value: "teamId",
        },
        fieldConverter: baseExportConverters.teamConverter,
        resultFieldname: "Team",
      },
    });
  }
}

//#region Converters

const unpublishedEditsConverter: FieldConverter = async (values: {
  publishedAt?: Date;
  agendaPublishedAt?: Date;
  updatedAt?: Date;
}) => {
  const isPublished = !!(values.agendaPublishedAt ?? values.publishedAt);

  if (!isPublished) {
    return "Unpublished Edits";
  }

  const publishedAt = values.agendaPublishedAt ?? values.updatedAt;
  const updatedAt = values.updatedAt;

  if (!publishedAt || !updatedAt) {
    return "N/A";
  }

  return publishedAt < updatedAt ? "Unpublished Edits" : "";
};

const agendaConverter: FieldConverter = async (values: {
  value?: "string";
}) => {
  const agendaId = values.value;
  if (!agendaId) {
    return "No Agenda Attached";
  }
  const tenantId = tenantStore.getState().id;
  const agendaInField = await firstValueFrom(
    agendaRepo.get(tenantId, agendaId)
  );
  return agendaInField?.name ?? "...";
};

//#endregion

export const meetingsExportService = new MeetingExportService();
